import React, {useCallback, useLayoutEffect, useMemo, useState} from 'react';
import {Alert, AlertTitle, Box, DialogTitle, Theme, ToggleButton, ToggleButtonGroup, Typography,} from '@mui/material';
import {useTranslation} from 'react-i18next';
import useIsMounted from '../../../../../../../hooks/use-is-mounted';
import {
    DashboardFilterParameter,
    DashboardViewFilterSectionFilters,
    WidgetForcedFilterRequirement,
} from "../../types";
import DashboardViewFilterSection from "../../components/filter-section";
import {setReduxDialogState} from "../../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from 'react-redux';
import Dialog from "../../../../../../../components/base/dialog";
import {getWidgetFilterParameters} from "../../utils/forced-filters";

export interface EditDashboardWidgetFiltersDialogProps {
    open: boolean;
    filters?: DashboardViewFilterSectionFilters;
    widgetTitle?: string;
    widgetParameters?: DashboardFilterParameter[];
    widgetParameterTitles?: Partial<Record<DashboardFilterParameter, string>>;
    forcedFilterRequirements?: WidgetForcedFilterRequirement[];
    mode?: 'edit' | 'add';
    onSave?: (filters?: DashboardViewFilterSectionFilters) => Promise<boolean>;
    onClose?: () => void;
    canEdit?: boolean;
}

const EditDashboardWidgetFiltersDialog = ({
                                              open,
                                              onClose,
                                              filters,
                                              onSave,
                                              canEdit = false,
                                              widgetTitle,
                                              widgetParameters,
                                              widgetParameterTitles,
                                              forcedFilterRequirements,
                                              mode = 'edit',
                                          }: EditDashboardWidgetFiltersDialogProps) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.dashboard-v2.edit-widget-filters'});
    const lockCustomFilters = (forcedFilterRequirements?.length ?? 0) > 0 || mode === 'add';
    const originalTab = useMemo<'dashboard' | 'custom'>(() => {
        if (lockCustomFilters) {
            return 'custom';
        }
        return filters ? 'custom' : 'dashboard';
    }, [filters, lockCustomFilters]);
    const [tab, setTab] = useState<'dashboard' | 'custom'>(originalTab);
    const [updating, setUpdating] = useState(false);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();

    const resolvedParameters = useMemo(
        () => getWidgetFilterParameters(widgetParameters, forcedFilterRequirements),
        [widgetParameters, forcedFilterRequirements],
    );

    const initialFilters = useMemo<DashboardViewFilterSectionFilters>(() => {
        if (filters) {
            return {
                ...filters,
                mergeWithParentFilters: filters.mergeWithParentFilters ?? (lockCustomFilters ? true : filters.mergeWithParentFilters),
            };
        }
        if (lockCustomFilters) {
            return {mergeWithParentFilters: true};
        }
        return {};
    }, [filters, lockCustomFilters]);

    /**
     * Resets the tab to the original tab when the dialog opens.
     */
    useLayoutEffect(() => {
        if (open) {
            setTab(originalTab);
        }
    }, [originalTab, open])

    /**
     * The sx style mixin for the condition's toggle button group.
     */
    const toggleButtonGroupMixin = useCallback<ThemeMixin<Theme>>((theme) => {
        return ({
            width: '100%',
            border: `1px solid rgba(var(--palette-common-onBackgroundChannel) / 0.23)`,
            '& .MuiToggleButton-root': {
                backgroundColor: 'transparent',
                borderColor: 'transparent !important',
                color: theme.palette.text.primary,
                height: 30,
                width: 'calc(50% - 4px)',
                marginBlock: 0.5,
                marginInline: 0.5,
                padding: 1,
            },
            '& .MuiToggleButton-root:hover': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.text.primary,
            },
            '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.primary.main,
            },
            '& .MuiToggleButton-root.Mui-selected:hover': {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
            },
        })
    }, []);

    /**
     * Handles closing the dialog without saving
     */
    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    /**
     * Persists filter changes via the parent save handler.
     */
    const setFilters = useCallback(async (newFilters?: DashboardViewFilterSectionFilters) => {
        if (lockCustomFilters && !newFilters) {
            return;
        }
        setUpdating(true);
        const resultFlag = await onSave?.(newFilters);
        if (!isMounted())
            return;
        setUpdating(false);
        if (resultFlag) {
            onClose?.();
        }
    }, [onSave, isMounted, onClose, lockCustomFilters]);

    /**
     * Saves the selected tab and updates the filters accordingly.
     */
    const saveTab = useCallback(async (value: 'dashboard' | 'custom') => {
        if (lockCustomFilters || value === tab || !value) {
            return;
        }
        if (value === 'dashboard') {
            if (originalTab !== value) {
                const confirmed = await new Promise((r) => {
                    dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                        open: true,
                        title: t(t => t.confirmFollowDashboard.title),
                        description: t(t => t.confirmFollowDashboard.description),
                        cancelButtonText: t(t => t.confirmFollowDashboard.cancel),
                        confirmButtonText: t(t => t.confirmFollowDashboard.confirm),
                        onClose: async (confirmed) => {
                            r(confirmed);
                            return true;
                        }
                    }));
                })
                if (!confirmed) {
                    return;
                }
                void setFilters(undefined);
            }
            setTab(value);
        } else {
            setTab(value);
        }
    }, [tab, originalTab, setFilters, dispatch, t, lockCustomFilters]);

    /**
     * No-op setter used when the filter section is mounted in view-only mode.
     */
    const noopSetFilters = useCallback((_value: DashboardViewFilterSectionFilters) => {
        // view-only
    }, []);

    /**
     * Resolves a human-readable filter title from the widget parameter metadata or i18n.
     */
    const getFilterTitle = useCallback((filterName: DashboardFilterParameter): string => {
        if (widgetParameterTitles?.[filterName]) {
            return widgetParameterTitles[filterName] as string;
        }
        if (filterName === DashboardFilterParameter.PartCategories) {
            return t(t => t.filterNames.PartCategories);
        }
        if (filterName === DashboardFilterParameter.Industry) {
            return t(t => t.filterNames.Industry);
        }
        return filterName;
    }, [t, widgetParameterTitles]);

    /**
     * Builds the human-readable constraint copy for a single requirement.
     */
    const getRequirementDetails = useCallback((requirement: WidgetForcedFilterRequirement): string => {
        const hasMin = requirement.requiredMinCount != null;
        const hasMax = requirement.requiredMaxCount != null;
        if (requirement.isRequired && hasMin && hasMax) {
            return t(t => t.requirements.detailsRequiredMinMax, {min: requirement.requiredMinCount, max: requirement.requiredMaxCount});
        }
        if (requirement.isRequired && hasMin) {
            return t(t => t.requirements.detailsRequiredMin, {min: requirement.requiredMinCount});
        }
        if (requirement.isRequired && hasMax) {
            return t(t => t.requirements.detailsRequiredMax, {max: requirement.requiredMaxCount});
        }
        if (requirement.isRequired) {
            return t(t => t.requirements.detailsRequired);
        }
        if (hasMin && hasMax) {
            return t(t => t.requirements.detailsMinMax, {min: requirement.requiredMinCount, max: requirement.requiredMaxCount});
        }
        if (hasMin) {
            return t(t => t.requirements.detailsMin, {min: requirement.requiredMinCount});
        }
        if (hasMax) {
            return t(t => t.requirements.detailsMax, {max: requirement.requiredMaxCount});
        }
        return '';
    }, [t]);

    const showCustomFilters = lockCustomFilters || (canEdit ? tab === 'custom' : originalTab === 'custom');
    const dialogMaxWidth = showCustomFilters ? 'lg' : 'sm';
    const dialogTitle = mode === 'add'
        ? t(t => t.title.add, {widgetTitle: widgetTitle ?? ''})
        : canEdit
            ? t(t => t.title.manage, {widgetTitle: widgetTitle ?? ''})
            : t(t => t.title.view, {widgetTitle: widgetTitle ?? ''});

    const requirementsAlert = Boolean(forcedFilterRequirements?.length) && (
        <Alert severity="info" sx={{mb: 2}}>
            <AlertTitle>{t(t => t.requirements.title)}</AlertTitle>
            <Typography variant="body2" sx={{mb: 1}}>
                {t(t => t.requirements.description)}
            </Typography>
            <Typography component="ul" variant="body2" sx={{m: 0, pl: 2}}>
                {
                    forcedFilterRequirements?.map((requirement) => (
                        <li key={requirement.filterName}>
                            {t(t => t.requirements.item, {
                                filterTitle: getFilterTitle(requirement.filterName),
                                details: getRequirementDetails(requirement),
                            })}
                        </li>
                    ))
                }
            </Typography>
            <Typography variant="caption" display="block" sx={{mt: 1}}>
                {t(t => t.requirements.cannotFollowDashboard)}
            </Typography>
        </Alert>
    );

    const leadingContent = (
        <>
            {requirementsAlert}
            {
                canEdit && showCustomFilters &&
                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    {t(t => t.editCustomModeDescription)}
                </Typography>
            }
            {
                !canEdit && !lockCustomFilters && originalTab === 'custom' &&
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    {t(t => t.followingCustomDescription)}
                </Typography>
            }
        </>
    );

    return (
        <Dialog
            open={open}
            setOpen={handleClose}
            maxWidth={dialogMaxWidth}
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    maxHeight: 'calc(100% - 64px)',
                    overflow: 'hidden',
                },
                '& .dialog-paper': {
                    overflow: 'hidden !important',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    maxHeight: '100%',
                },
            }}
        >
            <Box sx={{flexShrink: 0, px: 3, pt: 1, pb: 1.5}}>
                <DialogTitle sx={{px: 0, pt: 1, pb: 1.5}}>
                    {dialogTitle}
                </DialogTitle>
                {
                    !lockCustomFilters &&
                    <ToggleButtonGroup
                        orientation="horizontal"
                        value={canEdit ? tab : originalTab}
                        exclusive
                        disabled={!canEdit}
                        onChange={(e, value) => saveTab(value)}
                        sx={toggleButtonGroupMixin}
                    >
                        <ToggleButton value={'dashboard'}>
                            <Typography variant={'overline'} fontWeight={600}>
                                {t(t => t.modeToggle.dashboard)}
                            </Typography>
                        </ToggleButton>
                        <ToggleButton value={'custom'}>
                            <Typography variant={'overline'} fontWeight={600}>
                                {t(t => t.modeToggle.custom)}
                            </Typography>
                        </ToggleButton>
                    </ToggleButtonGroup>
                }
            </Box>
            {
                canEdit && !lockCustomFilters && tab === 'dashboard' &&
                <Box sx={{flex: 1, minHeight: 0, overflow: 'auto', px: 3, pb: 3}}>
                    <Typography variant="body2" color="text.secondary" textAlign={'center'} marginBlock={4}>
                        {t(t => t.editDashboardModeDescription)}
                    </Typography>
                </Box>
            }
            {
                !canEdit && !lockCustomFilters && originalTab === 'dashboard' &&
                <Box sx={{flex: 1, minHeight: 0, overflow: 'auto', px: 3, pb: 3}}>
                    <Typography variant="body2" color="text.secondary" textAlign={'center'} marginBlock={2}>
                        {t(t => t.followingDashboardDescription)}
                    </Typography>
                </Box>
            }
            {
                canEdit && showCustomFilters &&
                <DashboardViewFilterSection
                    visible
                    dialogLayout
                    leadingContent={leadingContent}
                    disabled={updating}
                    filters={initialFilters}
                    parameters={resolvedParameters}
                    setFilters={setFilters}
                    allowApplyingWithoutChange
                    allowMergingWithParentFilters
                    forcedFilterRequirements={forcedFilterRequirements}
                />
            }
            {
                !canEdit && showCustomFilters &&
                <DashboardViewFilterSection
                    visible
                    viewOnly
                    disabled
                    dialogLayout
                    leadingContent={leadingContent}
                    filters={initialFilters}
                    parameters={resolvedParameters}
                    setFilters={noopSetFilters}
                    allowMergingWithParentFilters
                    forcedFilterRequirements={forcedFilterRequirements}
                />
            }
        </Dialog>
    );
};

export default EditDashboardWidgetFiltersDialog;
